'use client'

import React, { useState, useRef, useEffect, KeyboardEvent, ClipboardEvent } from 'react'
import { cn } from '../../lib/utils'

interface InputOTPProps {
  value: string
  onChange: (value: string) => void
  disabled?: boolean
  className?: string
  numInputs?: number
  placeholder?: string
  id?: string
}

interface InputOTPSlotProps {
  index: number
  value: string
  isActive: boolean
  disabled?: boolean
  onChange: (index: number, value: string) => void
  onKeyDown: (index: number, e: KeyboardEvent<HTMLInputElement>) => void
  onFocus: (index: number) => void
  onPaste: (e: ClipboardEvent<HTMLInputElement>) => void
  id?: string
}

const InputOTPSlot: React.FC<InputOTPSlotProps> = ({
  index,
  value,
  isActive,
  disabled,
  onChange,
  onKeyDown,
  onFocus,
  onPaste,
  id
}) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [isAnimating, setIsAnimating] = useState(false)
  const prevValueRef = useRef(value)

  useEffect(() => {
    if (isActive && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isActive])

  // Trigger bounce animation when value changes from empty to filled
  useEffect(() => {
    if (value && !prevValueRef.current) {
      setIsAnimating(true)
      const timer = setTimeout(() => setIsAnimating(false), 150)
      return () => clearTimeout(timer)
    }
    prevValueRef.current = value
  }, [value])

  return (
    <input
      ref={inputRef}
      id={id}
      name={id ? `${id}-slot-${index}` : `otp-slot-${index}`}
      type="text"
      inputMode="numeric"
      pattern="[0-9]*"
      maxLength={1}
      value={value}
      disabled={disabled}
      className={cn(
        'relative flex h-14 w-12 items-center justify-center border border-field text-center text-xl font-mono rounded-md bg-background dark:bg-white/5 text-gray-900 dark:text-foreground transition-all duration-150',
        'focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20',
        'disabled:cursor-not-allowed disabled:opacity-50',
        isActive && 'border-primary ring-2 ring-primary/20',
        value && 'border-primary'
      )}
      style={{
        transform: isAnimating ? 'scale(1.12)' : 'scale(1)',
        transition: 'transform 0.15s cubic-bezier(0.68, -0.55, 0.265, 1.55)'
      }}
      onChange={(e) => {
        const newValue = e.target.value.replace(/\D/g, '')
        onChange(index, newValue)
        
        // Hide cursor immediately after entering a digit
        if (newValue && inputRef.current) {
          inputRef.current.blur()
        }
      }}
      onKeyDown={(e) => onKeyDown(index, e)}
      onFocus={() => onFocus(index)}
      onBlur={() => onFocus(-1)} // Clear active state when input loses focus
      onPaste={onPaste}
      autoComplete="one-time-code"
    />
  )
}

export const InputOTP: React.FC<InputOTPProps> = ({
  value,
  onChange,
  disabled = false,
  className,
  numInputs = 6,
  placeholder: _placeholder = '••••••',
  id
}) => {
  const [activeIndex, setActiveIndex] = useState(0)
  const values = Array.from({ length: numInputs }, (_, i) => value[i] || '')
  const focusTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isUserInputRef = useRef(false)

  const handleChange = (index: number, newValue: string) => {
    if (newValue.length > 1) return

    const newValues = [...values]
    newValues[index] = newValue

    const newOTP = newValues.join('')
    isUserInputRef.current = true // Mark as user input
    onChange(newOTP)

    // Auto-focus next input after bounce animation completes
    if (newValue && index < numInputs - 1) {
      // Clear focus immediately when input is blurred
      setActiveIndex(-1)
      
      // Clear any existing focus timeout for rapid typing
      if (focusTimeoutRef.current) {
        clearTimeout(focusTimeoutRef.current)
      }
      
      // Delay focus to let the bounce animation complete
      focusTimeoutRef.current = setTimeout(() => {
        setActiveIndex(index + 1)
        focusTimeoutRef.current = null
      }, 320) // Wait for bounce animation to fully complete (300ms + small buffer)
    } else if (newValue && index === numInputs - 1) {
      // If last digit entered, remove focus state immediately
      setActiveIndex(-1) // No cell should be active when OTP is complete
      
      // Clear any existing focus timeout
      if (focusTimeoutRef.current) {
        clearTimeout(focusTimeoutRef.current)
        focusTimeoutRef.current = null
      }
    }
  }

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      e.preventDefault()
      
      if (values[index]) {
        // Clear current slot
        handleChange(index, '')
      } else if (index > 0) {
        // Move to previous slot and clear it
        setActiveIndex(index - 1)
        handleChange(index - 1, '')
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault()
      setActiveIndex(index - 1)
    } else if (e.key === 'ArrowRight' && index < numInputs - 1) {
      e.preventDefault()
      setActiveIndex(index + 1)
    } else if (e.key === 'Delete') {
      e.preventDefault()
      handleChange(index, '')
    }
  }

  const handleFocus = (index: number) => {
    // Don't allow focus on specific cells if OTP is complete, but allow clearing (-1)
    if (value.length === numInputs && index >= 0) {
      return
    }
    setActiveIndex(index)
  }

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault()
    const pasteData = e.clipboardData.getData('text/plain').replace(/\D/g, '')
    
    if (pasteData) {
      const newOTP = pasteData.slice(0, numInputs)
      isUserInputRef.current = true // Mark as user input
      onChange(newOTP)
      
      // Focus the last filled slot or the next empty one
      const nextIndex = Math.min(newOTP.length, numInputs - 1)
      setActiveIndex(nextIndex)
    }
  }

  // Reset active index when value changes externally (not from user input)
  useEffect(() => {
    if (isUserInputRef.current) {
      // Reset the flag for next time
      isUserInputRef.current = false
      return
    }
    
    // Only handle external value changes
    if (value.length === 0) {
      setActiveIndex(0)
    } else if (value.length < numInputs) {
      // Position focus at the next empty cell
      setActiveIndex(value.length)
    } else {
      // If all cells are filled, focus the last cell
      setActiveIndex(numInputs - 1)
    }
  }, [value, numInputs])

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (focusTimeoutRef.current) {
        clearTimeout(focusTimeoutRef.current)
      }
    }
  }, [])

  // Find the first empty cell index
  const firstEmptyIndex = values.findIndex(v => !v)
  const isComplete = firstEmptyIndex === -1

  return (
    <div className={cn('flex items-center gap-3', className)}>
      <div className="flex items-center gap-2">
        {Array.from({ length: numInputs }, (_, index) => {
          // Disable cells that come after the first empty cell
          const shouldDisable = disabled || (!isComplete && index > firstEmptyIndex)
          
          return (
            <InputOTPSlot
              key={index}
              index={index}
              value={values[index]}
              isActive={activeIndex === index}
              disabled={shouldDisable}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              onFocus={handleFocus}
              onPaste={handlePaste}
              id={index === 0 ? id : undefined}
            />
          )
        })}
      </div>
    </div>
  )
}

export type { InputOTPProps } 